export type TreeMutationWriterClass =
  | 'DESIGNATED_GATEWAY'
  | 'USER_RUNTIME'
  | 'CLAIM_RUNTIME'
  | 'GOVERNED_REPAIR'
  | 'BACKGROUND_RECONCILIATION'
  | 'ROLLBACK_CLEANUP'
  | 'DEBUG_REPAIR'
  | 'LEGACY_BYPASS';

export interface TreeMutationWriterRecord {
  id: string;
  path: string;
  classification: TreeMutationWriterClass;
  writesFamilyGraphMembers: boolean;
  currentAuthority: boolean;
  productionReachable: boolean;
  requiresMigration: boolean;
  notes: string;
}

export const TREE_MUTATION_WRITER_REGISTRY:
  readonly TreeMutationWriterRecord[] = [
    {
      id: 'graph-mutation-gateway',
      path: 'src/lib/graph/graphMutationGateway.ts',
      classification: 'DESIGNATED_GATEWAY',
      writesFamilyGraphMembers: true,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Sole active browser direct-writer file for non-identity Tree mutation. Claim establishment, release, and rebind are excluded from browser authority and require governed server transactions.',
    },
    {
      id: 'family-graph-context',
      path: 'src/contexts/FamilyGraphContext.tsx',
      classification: 'USER_RUNTIME',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Production Tree mutation orchestrator. Browser claim DML is retired; identity binding routes only to governed server transactions while ordinary Tree mutation remains gateway-governed.',
    },
    {
      id: 'add-relative-universal',
      path: 'src/lib/addRelativeUniversal.ts',
      classification: 'ROLLBACK_CLEANUP',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Duplicate-spouse cleanup delegates to the governed non-interactive rollback authority supplied by FamilyGraphContext and performs no direct family_graph_members DML.',
    },
    {
      id: 'connect-user-modal',
      path: 'src/components/familyGraph/ConnectUserModal.tsx',
      classification: 'CLAIM_RUNTIME',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Performs read-only target resolution and delegates claim metadata mutation to the authenticated transactional connect-user-to-profile authority.',
    },
    {
      id: 'family-graph-section',
      path: 'src/components/familyGraph/FamilyGraphSection.tsx',
      classification: 'DEBUG_REPAIR',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Detects duplicate self-anchor candidates and emits review evidence without moving media, rewriting ancestry, or deleting Tree rows.',
    },
    {
      id: 'graph-debug-panel',
      path: 'src/components/familyGraph/GraphDebugPanel.tsx',
      classification: 'DEBUG_REPAIR',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Provides dev and staging diagnostics and emits reviewed repair evidence without moving media, rewriting ancestry, linking spouses, or deleting members.',
    },
    {
      id: 'repair-actions',
      path: 'src/lib/intelligence/repairActions.ts',
      classification: 'GOVERNED_REPAIR',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Builds Tree repair proposals and emits reviewed server-repair evidence; only the separate invite-status action remains executable.',
    },
    {
      id: 'orphan-in-law-repair',
      path: 'src/lib/orphanInLawRepair.ts',
      classification: 'GOVERNED_REPAIR',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Detects orphan in-law candidates and emits transactional repair review evidence without moving media, rewriting relationships, or deleting members.',
    },
    {
      id: 'reconciliation',
      path: 'src/lib/reconciliation.ts',
      classification: 'BACKGROUND_RECONCILIATION',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Runs from ResilienceMonitor in permanent observation-only mode and reports spouse inconsistencies without mutating family_graph_members.',
    },
    {
      id: 'invite-self-heal',
      path: 'src/lib/inviteSelfHeal.ts',
      classification: 'BACKGROUND_RECONCILIATION',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Reads Tree claim state, restores missing profile and invite acceptance metadata, and explicitly delegates all family_graph_members creation to the governed onboarding pipeline.',
    },
    {
      id: 'system-health',
      path: 'src/pages/SystemHealth.tsx',
      classification: 'DEBUG_REPAIR',
      writesFamilyGraphMembers: false,
      currentAuthority: true,
      productionReachable: true,
      requiresMigration: false,
      notes:
        'Provides operator-facing Tree diagnostics through read-only queries and local cache cleanup; non-Tree repairs delegate to separate governed Edge Functions.',
    },
  ] as const;

export type TreeServerMutationAuthorityKind =
  | 'RPC'
  | 'TRIGGER';

export type TreeServerMutationLifecycle =
  | 'ACTIVE_CALLER'
  | 'DORMANT_NO_REPOSITORY_CALLER'
  | 'RETIRED_REVOKED'
  | 'IMPLICIT_TRIGGER';

export type TreeServerMutationInvocationAuthority =
  | 'AUTHENTICATED_AND_SERVICE_ROLE'
  | 'AUTHENTICATED_ONLY'
  | 'SERVICE_ROLE_ONLY'
  | 'POSTGRES_ONLY_FAIL_CLOSED'
  | 'DATABASE_TRIGGER';

export interface TreeServerMutationAuthorityRecord {
  readonly id: string;
  readonly functionName: string;
  readonly functionDefinitionPath: string;
  readonly triggerDefinitionPath?: string;
  readonly kind:
    TreeServerMutationAuthorityKind;
  readonly lifecycle:
    TreeServerMutationLifecycle;
  readonly invocationAuthority:
    TreeServerMutationInvocationAuthority;
  readonly securityDefiner: boolean;
  readonly repositoryCallSites: number;
  readonly currentAuthority: boolean;
  readonly requiresRetirementReview: boolean;
  readonly notes: string;
}

export interface TreeMemberTriggerValidatorRecord {
  readonly triggerName: string;
  readonly functionName: string;
  readonly functionDefinitionPath: string;
  readonly triggerDefinitionPath: string;
  readonly securityDefiner: boolean;
  readonly currentAuthority: boolean;
}

export const TREE_MUTATION_BROWSER_AUTHORITY =
  Object.freeze({
    directWriterPath:
      'src/lib/graph/graphMutationGateway.ts',
    directWriterFiles: 1,
    directWriteSites: 4,
    nonGatewayDirectWriterFiles: 0,
    runtimeConsumerPath:
      'src/contexts/FamilyGraphContext.tsx',
    runtimeConsumers: 1,
    runtimeCallSites: 9,
  } as const);

export const TREE_SERVER_MUTATION_AUTHORITY_REGISTRY:
  readonly TreeServerMutationAuthorityRecord[] = [
    {
      id: 'legacy-invite-acceptance',
      functionName:
        'accept_invite_and_join_tree',
      functionDefinitionPath:
        'supabase/migrations/20260723020000_cc_db_repro_001_production_schema_baseline.sql',
      kind: 'RPC',
      lifecycle:
        'RETIRED_REVOKED',
      invocationAuthority:
        'POSTGRES_ONLY_FAIL_CLOSED',
      securityDefiner: true,
      repositoryCallSites: 0,
      currentAuthority: false,
      requiresRetirementReview: false,
      notes:
        'CC-TREE-LIVE-003G3H revokes application execution because this legacy RPC can correlate by normalized name and establish claim identity.',
    },
    {
      id: 'guided-invite-acceptance',
      functionName:
        'accept_invite_and_join_tree_guided',
      functionDefinitionPath:
        'supabase/migrations/20260817000056_cc_tree_live_003g3k_person_targeted_invite_execution_authority.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_AND_SERVICE_ROLE',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'CC-TREE-LIVE-003G3WH4 preserves this RPC only for person-targeted invite acceptance. RelationshipOnboardingModal retains exactly one targeted caller; the historical generic branch is fail-closed and has no repository caller.',
    },
    {
      id: 'generic-canonical-invite-acceptance',
      functionName:
        'accept_generic_invite_canonical_atomic',
      functionDefinitionPath:
        'supabase/migrations/20260901070700_cc_tree_live_003g3wh4_generic_invite_canonical_convergence_green_runtime_implementation.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'CC-TREE-LIVE-003G3WH4 generic invite create-new convergence authority. CanonicalGenericInviteAcceptanceRuntime is the sole repository caller; it reuses canonical B/C/E, performs exactly one generic RPC, forbids generic existing-person binding, and requires the durable canonical binding certificate.',
    },
    {
      id: 'targeted-sponsored-invite-pretest',
      functionName:
        'simulate_sponsored_invite_e2e',
      functionDefinitionPath:
        'supabase/migrations/20260817000056_cc_tree_live_003g3k_person_targeted_invite_execution_authority.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'GovernorCommandCenter invokes this authenticated rollback-only targeted sponsored-seat pretest. Synthetic Tree and invite writes execute only inside an intentionally aborted PL/pgSQL subtransaction, so no synthetic production record may commit.',
    },
    {
      id: 'connect-user-claim',
      functionName:
        'governed_connect_user_to_profile',
      functionDefinitionPath:
        'supabase/migrations/20260724_cc_tree_000b1e9b9_connect_user_claim_authority.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'SERVICE_ROLE_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'The connect-user-to-profile Edge Function invokes this service-role-only claim authority.',
    },
    {
      id: 'owner-self-profile-claim',
      functionName:
        'claim_own_tree_self_profile',
      functionDefinitionPath:
        'supabase/migrations/20260816173735_cc_tree_live_003g3h_claim_identity_transition_barrier.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'Narrow authenticated owner-self bootstrap. Requires auth.uid() to own the exact self placeholder, rejects ambiguity and existing conflicting claims, and cannot claim another account placecard.',
    },
    {
      id: 'parent-edge-repair',
      functionName:
        'governed_replace_parent_edges',
      functionDefinitionPath:
        'supabase/migrations/20260723021000_cc_tree_000b1e9a_parent_delete_protection.sql',
      kind: 'RPC',
      lifecycle:
        'DORMANT_NO_REPOSITORY_CALLER',
      invocationAuthority:
        'SERVICE_ROLE_ONLY',
      securityDefiner: true,
      repositoryCallSites: 0,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'Service-role-only parent-edge repair authority with no active repository runtime caller.',
    },
    {
      id:
        'physically-absent-parent-reference-retirement',
      functionName:
        'execute_tree_physically_absent_parent_reference_retirement',
      functionDefinitionPath:
        'supabase/migrations/20260816023502_cc_tree_live_003g3f_physically_absent_parent_reference_execution.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'SERVICE_ROLE_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'CC-TREE-LIVE-003G3F authenticated Edge Function invokes this narrow service-role executor only after verified JWT actor derivation, exact cohort commitment parity, 003G2U planning eligibility, atomic post-state verification, and aggregate-only response validation.',
    },
    {
      id: 'existing-tree-repair-executor',
      functionName:
        'execute_existing_tree_repair',
      functionDefinitionPath:
        'supabase/migrations/20260817222600_cc_tree_live_003g3n_existing_tree_repair_execution_implementation.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'SERVICE_ROLE_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'CC-TREE-LIVE-003G3N verified-JWT Edge Function invokes this service-role-only atomic existing-Tree repair executor. Owner request, claimant consent where required, exact before-state, stale-state protection, private audit evidence, and post-state certification are revalidated inside the transaction. Source inclusion does not authorize production installation or live execution.',
    },
    {
      id: 'member-restoration',
      functionName:
        'restore_family_graph_member',
      functionDefinitionPath:
        'supabase/migrations/20260723020000_cc_db_repro_001_production_schema_baseline.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_AND_SERVICE_ROLE',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'graphMutationGateway delegates governed member restoration to this authenticated transaction.',
    },
    {
      id: 'error-only-member-deletion',
      functionName:
        'soft_delete_family_graph_member',
      functionDefinitionPath:
        'supabase/migrations/20260804184700_cc_tree_live_003f2c4b1_error_only_delete_server.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_AND_SERVICE_ROLE',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'graphMutationGateway delegates certified error-only soft deletion to this authenticated transaction.',
    },
    {
      id: 'atomic-existing-ancestry',
      functionName:
        'connect_existing_ancestry_atomic',
      functionDefinitionPath:
        'supabase/migrations/20260812224057_cc_tree_live_003g3b_atomic_existing_ancestry_hardening.sql',
      kind: 'RPC',
      lifecycle: 'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'graphMutationGateway invokes the authenticated atomic existing-ancestry writer. Service-role and anonymous execution remain revoked.',
    },
    {
      id: 'canonical-fact-plan-atomic-writer',
      functionName:
        'execute_canonical_tree_fact_plan_atomic',
      functionDefinitionPath:
        'supabase/migrations/20260829011000_cc_tree_live_003g3wf5b1_transport_incident_repair.sql',
      kind: 'RPC',
      lifecycle:
        'ACTIVE_CALLER',
      invocationAuthority:
        'AUTHENTICATED_ONLY',
      securityDefiner: true,
      repositoryCallSites: 1,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'CC-TREE-LIVE-003G3WF5A effective E v2 source authority. The shared B/C/E runtime adapter remains the sole repository caller; repository call sites remain one. Add Relative and Set Relationship remain unwired pending separately governed convergence stages.',
    },
    {
      id: 'atomic-partnership-lifecycle',
      functionName:
        'transition_tree_partnership_atomic',
      functionDefinitionPath:
        'supabase/migrations/20260813025753_cc_tree_live_003g3c_atomic_partnership_server_writer_fail_closed.sql',
      kind: 'RPC',
      lifecycle:
        'DORMANT_NO_REPOSITORY_CALLER',
      invocationAuthority:
        'POSTGRES_ONLY_FAIL_CLOSED',
      securityDefiner: true,
      repositoryCallSites: 0,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'Installed fail-closed atomic partnership lifecycle capability. Public, anon, authenticated, and service_role EXECUTE remain revoked pending a separately governed activation milestone.',
    },
    {
      id: 'reciprocal-spouse-trigger',
      functionName:
        'sync_spouse_link',
      functionDefinitionPath:
        'supabase/migrations/20260813025753_cc_tree_live_003g3c_atomic_partnership_server_writer_fail_closed.sql',
      triggerDefinitionPath:
        'supabase/migrations/20260723020000_cc_db_repro_001_production_schema_baseline.sql',
      kind: 'TRIGGER',
      lifecycle: 'IMPLICIT_TRIGGER',
      invocationAuthority:
        'DATABASE_TRIGGER',
      securityDefiner: false,
      repositoryCallSites: 0,
      currentAuthority: true,
      requiresRetirementReview: false,
      notes:
        'Enabled trg_sync_spouse_link performs reciprocal spouse maintenance after member insert or spouse_id update.',
    },
  ] as const;

export const TREE_MEMBER_TRIGGER_VALIDATOR_REGISTRY:
  readonly TreeMemberTriggerValidatorRecord[] = [
    {
      triggerName:
        'family_graph_members_claimed_profile_ownership',
      functionName:
        'enforce_claimed_profile_ownership',
      functionDefinitionPath:
        'supabase/migrations/20260817222600_cc_tree_live_003g3n_existing_tree_repair_execution_implementation.sql',
      triggerDefinitionPath:
        'supabase/migrations/20260723020000_cc_db_repro_001_production_schema_baseline.sql',
      securityDefiner: false,
      currentAuthority: true,
    },
    {
      triggerName:
        'family_graph_members_claim_identity_transition_authority',
      functionName:
        'enforce_claim_identity_transition_authority',
      functionDefinitionPath:
        'supabase/migrations/20260817222600_cc_tree_live_003g3n_existing_tree_repair_execution_implementation.sql',
      triggerDefinitionPath:
        'supabase/migrations/20260816173735_cc_tree_live_003g3h_claim_identity_transition_barrier.sql',
      securityDefiner: false,
      currentAuthority: true,
    },
    {
      triggerName:
        'family_graph_members_parent_edge_preservation',
      functionName:
        'enforce_parent_edge_preservation',
      functionDefinitionPath:
        'supabase/migrations/20260816023502_cc_tree_live_003g3f_physically_absent_parent_reference_execution.sql',
      triggerDefinitionPath:
        'supabase/migrations/20260723021000_cc_tree_000b1e9a_parent_delete_protection.sql',
      securityDefiner: true,
      currentAuthority: true,
    },
    {
      triggerName:
        'fgm_relationship_history_soft_delete_guard',
      functionName:
        'enforce_relationship_history_endpoint_retention',
      functionDefinitionPath:
        'supabase/migrations/20260813025753_cc_tree_live_003g3c_atomic_partnership_server_writer_fail_closed.sql',
      triggerDefinitionPath:
        'supabase/migrations/20260813035639_cc_tree_live_003g3c_atomic_partnership_postinstall_hardening.sql',
      securityDefiner: true,
      currentAuthority: true,
    },
  ] as const;

export function getProductionTreeMutationWriters():
  readonly TreeMutationWriterRecord[] {
  return TREE_MUTATION_WRITER_REGISTRY.filter(
    (writer) =>
      writer.productionReachable &&
      writer.writesFamilyGraphMembers,
  );
}

export function getUnauthorizedTreeMutationWriters():
  readonly TreeMutationWriterRecord[] {
  return TREE_MUTATION_WRITER_REGISTRY.filter(
    (writer) =>
      writer.productionReachable &&
      writer.writesFamilyGraphMembers &&
      !writer.currentAuthority,
  );
}

export function buildTreeMutationAuthoritySummary() {
  const writers =
    TREE_MUTATION_WRITER_REGISTRY;

  const productionWriters =
    getProductionTreeMutationWriters();

  const unauthorizedWriters =
    getUnauthorizedTreeMutationWriters();

  const browser =
    TREE_MUTATION_BROWSER_AUTHORITY;

  const serverAuthorities =
    TREE_SERVER_MUTATION_AUTHORITY_REGISTRY;

  const activeServerWriters =
    serverAuthorities.filter(
      (authority) =>
        authority.lifecycle ===
        'ACTIVE_CALLER',
    );

  const dormantServerWriters =
    serverAuthorities.filter(
      (authority) =>
        authority.lifecycle ===
        'DORMANT_NO_REPOSITORY_CALLER',
    );

  const retiredServerWriters =
    serverAuthorities.filter(
      authority => authority.lifecycle === 'RETIRED_REVOKED',
    );

  const implicitTriggerWriters =
    serverAuthorities.filter(
      (authority) =>
        authority.lifecycle ===
        'IMPLICIT_TRIGGER',
    );

  const activeServerRpcCallSites =
    activeServerWriters.reduce(
      (total, authority) =>
        total +
        authority.repositoryCallSites,
      0,
    );

  const centralized =
    unauthorizedWriters.length === 0 &&
    productionWriters.length === 1 &&
    browser.directWriterFiles === 1 &&
    browser.directWriteSites === 4 &&
    browser.nonGatewayDirectWriterFiles === 0 &&
    browser.runtimeConsumers === 1 &&
    browser.runtimeCallSites === 9;

  const certified =
    centralized &&
    serverAuthorities
      .filter(authority => authority.lifecycle !== 'RETIRED_REVOKED')
      .every(authority => authority.currentAuthority) &&
    retiredServerWriters.length === 1 &&
    retiredServerWriters.every(authority => !authority.currentAuthority) &&
    activeServerWriters.length === 10 &&
    dormantServerWriters.length === 2 &&
    implicitTriggerWriters.length === 1 &&
    activeServerRpcCallSites === 11 &&
    TREE_MEMBER_TRIGGER_VALIDATOR_REGISTRY
      .every(
        (validator) =>
          validator.currentAuthority,
      );

  return {
    totalWriters:
      writers.length,
    productionWriters:
      productionWriters.length,
    unauthorizedWriters:
      unauthorizedWriters.length,
    confirmedDirectWriterFiles:
      browser.directWriterFiles,
    confirmedDirectWriteSites:
      browser.directWriteSites,
    nonGatewayDirectWriterFiles:
      browser.nonGatewayDirectWriterFiles,
    gatewayRuntimeConsumers:
      browser.runtimeConsumers,
    gatewayRuntimeCallSites:
      browser.runtimeCallSites,
    activeServerWriterFunctions:
      activeServerWriters.length,
    dormantServerWriterFunctions:
      dormantServerWriters.length,
    retiredServerWriterFunctions:
      retiredServerWriters.length,
    implicitTriggerWriterFunctions:
      implicitTriggerWriters.length,
    effectiveServerWriterFunctions:
      serverAuthorities.length,
    activeServerRpcCallSites,
    enabledTreeMemberTriggers:
      implicitTriggerWriters.length +
      TREE_MEMBER_TRIGGER_VALIDATOR_REGISTRY.length,
    triggerValidators:
      TREE_MEMBER_TRIGGER_VALIDATOR_REGISTRY.length,
    centralized,
    certificationStatus:
      certified
        ? 'CERTIFIED'
        : 'UNVERIFIED',
    status:
      certified
        ? 'CERTIFIED'
        : 'UNVERIFIED',
  } as const;
}
